import { useState } from "react";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  ExternalLink,
  FileText,
  RotateCcw,
} from "lucide-react";
import { Button } from "../ui/button";

export interface ReceiptMetadata {
  referenceNumber?: string;
  depositorName?: string;
  amount?: string;
  currency?: string;
  transferDate?: string;
  notes?: string;
}

export interface ReceiptViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiptUrl: string | null | undefined;
  title?: string;
  metadata?: ReceiptMetadata | null;
}

export function ReceiptViewerModal({
  open,
  onOpenChange,
  receiptUrl,
  title = "Bank Deposit Receipt",
  metadata,
}: ReceiptViewerModalProps) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  if (!open || !receiptUrl) return null;

  const isPdf =
    receiptUrl.toLowerCase().endsWith(".pdf") ||
    receiptUrl.toLowerCase().includes("application/pdf");

  const handleZoomIn = () => setScale((s) => Math.min(3, s + 0.25));
  const handleZoomOut = () => setScale((s) => Math.max(0.5, s - 0.25));
  const handleReset = () => {
    setScale(1);
    setRotation(0);
  };
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-xs p-3 sm:p-6 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 cursor-pointer"
        onClick={() => onOpenChange(false)}
      />

      {/* Modal Dialog */}
      <div className="relative z-50 w-full max-w-4xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header with Title & Action Controls */}
        <div className="px-5 py-3.5 border-b border-border/80 bg-muted/40 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <FileText className="h-4 w-4" />
            </div>
            <div className="truncate">
              <h3 className="text-sm font-bold text-foreground truncate">{title}</h3>
              {metadata?.referenceNumber && (
                <p className="text-[11px] text-muted-foreground font-mono truncate">
                  Ref: <span className="text-foreground font-semibold">{metadata.referenceNumber}</span>
                  {metadata.depositorName && ` • Depositor: ${metadata.depositorName}`}
                </p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            {!isPdf && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-xs cursor-pointer"
                  onClick={handleZoomOut}
                  title="Zoom Out (-25%)"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-[11px] font-mono px-1 text-muted-foreground select-none w-12 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-xs cursor-pointer"
                  onClick={handleZoomIn}
                  title="Zoom In (+25%)"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-xs cursor-pointer"
                  onClick={handleRotate}
                  title="Rotate 90° Clockwise"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-xs cursor-pointer"
                  onClick={handleReset}
                  title="Reset Zoom & Rotation"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </>
            )}

            <a
              href={receiptUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-xs"
              title="Download Receipt"
            >
              <Download className="h-4 w-4" />
            </a>

            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-xs"
              title="Open in New Tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer ml-1"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Optional Metadata Bar */}
        {metadata && (
          <div className="px-5 py-2.5 bg-muted/20 border-b border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            {metadata.depositorName && (
              <div>
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Depositor
                </span>
                <span className="font-semibold text-foreground truncate block">
                  {metadata.depositorName}
                </span>
              </div>
            )}
            {metadata.referenceNumber && (
              <div>
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Reference #
                </span>
                <span className="font-mono font-bold text-foreground truncate block">
                  {metadata.referenceNumber}
                </span>
              </div>
            )}
            {metadata.amount && (
              <div>
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Expected / Transferred
                </span>
                <span className="font-semibold text-primary block">
                  {metadata.currency || "LKR"} {metadata.amount}
                </span>
              </div>
            )}
            {metadata.transferDate && (
              <div>
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Date
                </span>
                <span className="text-muted-foreground block truncate">
                  {new Date(metadata.transferDate).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content Viewer Body */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-zinc-950/40 min-h-[380px] max-h-[68vh]">
          {isPdf ? (
            <iframe
              src={receiptUrl}
              title="PDF Receipt Viewer"
              className="w-full h-full min-h-[500px] rounded-lg border border-border"
            />
          ) : (
            <div className="overflow-auto max-w-full max-h-full flex items-center justify-center">
              <img
                src={receiptUrl}
                alt="Bank Receipt Slip"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transition: "transform 0.15s ease-out",
                }}
                className="max-w-full max-h-[60vh] object-contain rounded-md shadow-md select-none"
              />
            </div>
          )}
        </div>

        {/* Footer info note */}
        <div className="px-5 py-2.5 border-t border-border/80 bg-muted/30 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Click outside or press Esc to close</span>
          <span>Encrypted Platform Asset</span>
        </div>
      </div>
    </div>
  );
}
